/**
 * Owns every piece of state + side-effect for /transactions. Returns a
 * flat bag the shell + views destructure.
 *
 * Logic preserved 1:1 from the original 1057-line Transactions.js:
 *   - GET /accounts + /transactions (with server-side search/account/date
 *     filtering) + /recurring on mount + filter change
 *   - Combined "items" pipeline: one-off + recurring -> filter (mechanic /
 *     transfer-collapse) -> sort by column
 *   - Add/edit dialog with mechanic toggle (one-off vs recurring), and a
 *     transfer toggle that flips between single-account and source/dest
 *     fields. POSTs to /transactions, /recurring, /transactions/transfer,
 *     or /recurring/transfer depending on the matrix.
 *   - Delete with optimistic-removal + undo window via useUndoDelete
 *   - CSV import with tier-gated upsell modal on 402/tier-required
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import useUndoDelete from '../../hooks/useUndoDelete';
import { toUIType, toDBType } from '../../lib/liabilityType';
import { tierRequiredDetail } from '../../components/TierGate';
import { emptyForm, MS_PER_DAY } from './constants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useTransactions() {
  const [transactions, setTransactions] = useState([]);
  const [recurringList, setRecurringList] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [mechanicFilter, setMechanicFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ column: 'date', direction: 'desc' });
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [importAccountId, setImportAccountId] = useState('');
  const [importGateDetail, setImportGateDetail] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const { scheduleDelete } = useUndoDelete();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState(emptyForm());

  const fetchData = useCallback(async () => {
    try {
      const accountsRes = await axios.get(`${API}/accounts`, { withCredentials: true });
      setAccounts(accountsRes.data);

      const params = new URLSearchParams();
      if (selectedAccount !== 'all') params.append('account_id', selectedAccount);
      if (searchQuery) params.append('search', searchQuery);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const [txRes, recRes] = await Promise.all([
        axios.get(`${API}/transactions?${params.toString()}`, { withCredentials: true }),
        axios.get(`${API}/recurring`, { withCredentials: true }),
      ]);
      setTransactions(txRes.data);
      setRecurringList(recRes.data);
    } catch (error) {
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, searchQuery, startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const accountNameMap = useMemo(
    () => Object.fromEntries(accounts.map((a) => [a.account_id, a.name])),
    [accounts],
  );
  const accountKindMap = useMemo(
    () => Object.fromEntries(accounts.map((a) => [a.account_id, a.kind || 'asset'])),
    [accounts],
  );

  // The kind of the account currently selected in the Add/Edit dialog —
  // drives the liability-aware label swap on the type dropdown.
  const formAccountKind = accountKindMap[formData.account_id] || 'asset';
  const formIsLiability = formAccountKind === 'liability';

  // Unified, filtered, sorted view
  const items = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const oneOffs = transactions.map((t) => ({
      key: `tx-${t.transaction_id}`,
      mechanic: 'one_off',
      id: t.transaction_id,
      date: t.date,
      description: t.description,
      amount: t.amount,
      transaction_type: t.transaction_type,
      category: t.category,
      account_id: t.account_id,
      reconciled: t.reconciled,
      is_estimated: !!t.is_estimated,
      transfer_id: t.transfer_id || null,
      transfer_role: t.transfer_role || null,
      transfer_counter_account_id: t.transfer_counter_account_id || null,
      raw: t,
    }));

    let recs = recurringList.map((r) => ({
      key: `rec-${r.recurring_id}`,
      mechanic: 'recurring',
      id: r.recurring_id,
      date: r.start_date,
      description: r.description,
      amount: r.amount,
      transaction_type: r.transaction_type,
      category: r.category,
      account_id: r.account_id,
      frequency: r.frequency,
      end_date: r.end_date,
      active: r.active,
      is_estimated: !!r.is_estimated,
      transfer_id: r.transfer_id || null,
      transfer_role: r.transfer_role || null,
      transfer_counter_account_id: r.transfer_counter_account_id || null,
      raw: r,
    }));

    // Apply same filters to recurring (txs are filtered on server)
    if (selectedAccount !== 'all') recs = recs.filter((r) => r.account_id === selectedAccount || r.transfer_counter_account_id === selectedAccount);
    if (q) recs = recs.filter((r) => (r.description || '').toLowerCase().includes(q) || (r.category || '').toLowerCase().includes(q));
    if (startDate) recs = recs.filter((r) => r.date >= startDate);
    if (endDate) recs = recs.filter((r) => r.date <= endDate);

    let combined = [...oneOffs, ...recs];
    if (mechanicFilter === 'one_off') combined = oneOffs;
    if (mechanicFilter === 'recurring') combined = recs;

    // For transfers, collapse to one row by keeping the SOURCE leg only.
    combined = combined.filter((it) => !it.transfer_id || it.transfer_role === 'source');

    if (mechanicFilter === 'transfer') combined = combined.filter((it) => !!it.transfer_id);

    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    const valueFor = (item) => {
      switch (sortConfig.column) {
        case 'description': return (item.description || '').toLowerCase();
        case 'frequency': return item.mechanic === 'recurring' ? (item.frequency || '') : 'zzz_one_off';
        case 'category': return (item.category || '').toLowerCase();
        case 'account': return (accountNameMap[item.account_id] || '').toLowerCase();
        case 'amount': {
          const uiType = toUIType(item.transaction_type, accountKindMap[item.account_id]);
          return (uiType === 'income' ? 1 : -1) * item.amount;
        }
        case 'date':
        default:
          return item.date || '';
      }
    };
    return combined.sort((a, b) => {
      const va = valueFor(a), vb = valueFor(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      // Tiebreaker: date desc so equal sort keys still feel chronological
      return (a.date < b.date ? 1 : -1);
    });
  }, [transactions, recurringList, mechanicFilter, selectedAccount, searchQuery, startDate, endDate, sortConfig, accountNameMap, accountKindMap]);

  const toggleSort = (column) => {
    setSortConfig((prev) => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'desc' ? 'asc' : 'desc' };
      }
      return { column, direction: column === 'date' || column === 'amount' ? 'desc' : 'asc' };
    });
  };

  const resetForm = () => setFormData(emptyForm());

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isRecurring = formData.mechanic === 'recurring';
    const isTransfer = !!formData.is_transfer;

    if (isTransfer && !editingItem) {
      if (!formData.source_account_id || !formData.destination_account_id) {
        toast.error('Pick both From and To accounts');
        return;
      }
      if (formData.source_account_id === formData.destination_account_id) {
        toast.error('Source and destination must differ');
        return;
      }
      if (!(formData.amount > 0)) {
        toast.error('Amount must be greater than zero');
        return;
      }
    }

    try {
      if (editingItem) {
        if (editingItem.mechanic === 'recurring') {
          const payload = {
            account_id: formData.account_id,
            description: formData.description,
            amount: formData.amount,
            transaction_type: toDBType(formData.transaction_type, accountKindMap[formData.account_id]),
            frequency: formData.frequency,
            start_date: formData.start_date,
            end_date: formData.end_date || null,
            category: formData.category || null,
            is_estimated: !!formData.is_estimated,
          };
          await axios.put(`${API}/recurring/${editingItem.id}`, payload, { withCredentials: true });
          toast.success(editingItem.is_transfer ? 'Transfer updated' : 'Recurring updated');
        } else {
          const payload = {
            account_id: formData.account_id,
            date: formData.date,
            description: formData.description,
            amount: formData.amount,
            transaction_type: toDBType(formData.transaction_type, accountKindMap[formData.account_id]),
            category: formData.category || null,
            is_estimated: !!formData.is_estimated,
          };
          await axios.put(`${API}/transactions/${editingItem.id}`, payload, { withCredentials: true });
          toast.success(editingItem.is_transfer ? 'Transfer updated' : 'Transaction updated');
        }
      } else if (isTransfer && isRecurring) {
        await axios.post(`${API}/recurring/transfer`, {
          source_account_id: formData.source_account_id,
          destination_account_id: formData.destination_account_id,
          amount: formData.amount,
          frequency: formData.frequency,
          start_date: formData.start_date,
          end_date: formData.end_date || null,
          description: formData.description || null,
          category: formData.category || null,
          is_estimated: !!formData.is_estimated,
        }, { withCredentials: true });
        toast.success('Recurring transfer created');
      } else if (isTransfer) {
        await axios.post(`${API}/transactions/transfer`, {
          source_account_id: formData.source_account_id,
          destination_account_id: formData.destination_account_id,
          date: formData.date,
          amount: formData.amount,
          description: formData.description || null,
          category: formData.category || null,
          is_estimated: !!formData.is_estimated,
        }, { withCredentials: true });
        toast.success('Transfer created');
      } else if (isRecurring) {
        const payload = {
          account_id: formData.account_id,
          description: formData.description,
          amount: formData.amount,
          transaction_type: toDBType(formData.transaction_type, accountKindMap[formData.account_id]),
          frequency: formData.frequency,
          start_date: formData.start_date,
          end_date: formData.end_date || null,
          category: formData.category || null,
          is_estimated: !!formData.is_estimated,
        };
        await axios.post(`${API}/recurring`, payload, { withCredentials: true });
        toast.success('Recurring created');
      } else {
        const payload = {
          account_id: formData.account_id,
          date: formData.date,
          description: formData.description,
          amount: formData.amount,
          transaction_type: toDBType(formData.transaction_type, accountKindMap[formData.account_id]),
          category: formData.category || null,
          is_estimated: !!formData.is_estimated,
        };
        await axios.post(`${API}/transactions`, payload, { withCredentials: true });
        toast.success('Transaction added');
      }
      fetchData();
      setDialogOpen(false);
      setEditingItem(null);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const handleEdit = (item) => {
    setEditingItem({ mechanic: item.mechanic, id: item.id, is_transfer: !!item.transfer_id });
    const itemKind = accountKindMap[item.account_id] || 'asset';
    const uiType = toUIType(item.transaction_type, itemKind);
    if (item.mechanic === 'recurring') {
      setFormData({
        mechanic: 'recurring',
        account_id: item.account_id,
        date: new Date().toISOString().split('T')[0],
        description: item.description,
        amount: item.amount,
        transaction_type: uiType,
        category: item.category || '',
        frequency: item.frequency,
        start_date: item.date,
        end_date: item.end_date || '',
        is_estimated: !!item.is_estimated,
        is_transfer: !!item.transfer_id,
        source_account_id: item.transfer_id ? item.account_id : '',
        destination_account_id: item.transfer_counter_account_id || '',
      });
    } else {
      setFormData({
        mechanic: 'one_off',
        account_id: item.account_id,
        date: item.date,
        description: item.description,
        amount: item.amount,
        transaction_type: uiType,
        category: item.category || '',
        frequency: 'monthly',
        start_date: new Date().toISOString().split('T')[0],
        end_date: '',
        is_estimated: !!item.is_estimated,
        is_transfer: !!item.transfer_id,
        source_account_id: item.transfer_id ? item.account_id : '',
        destination_account_id: item.transfer_counter_account_id || '',
      });
    }
    setDialogOpen(true);
  };

  const handleDelete = (item) => {
    setPendingDelete(item);
  };

  const confirmDelete = () => {
    const item = pendingDelete;
    if (!item) return;
    setPendingDelete(null);

    // 1. Optimistically remove from the local list
    if (item.mechanic === 'recurring') {
      setRecurringList((prev) => prev.filter((r) => r.recurring_id !== item.id));
    } else {
      setTransactions((prev) => prev.filter((t) => t.transaction_id !== item.id));
    }

    // 2. Schedule the actual DELETE with an undo window
    const endpoint = item.mechanic === 'recurring' ? 'recurring' : 'transactions';
    const label = item.mechanic === 'recurring' ? 'Recurring' : 'Transaction';
    scheduleDelete({
      id: item.id,
      label,
      executor: () => axios.delete(`${API}/${endpoint}/${item.id}`, { withCredentials: true }),
      onUndo: () => {
        if (item.mechanic === 'recurring') {
          setRecurringList((prev) => [item.raw, ...prev]);
        } else {
          setTransactions((prev) => [item.raw, ...prev]);
        }
      },
      onAfterDelete: fetchData,
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) setImportFile(file);
  };

  const handleImport = async () => {
    if (!importFile || !importAccountId) {
      toast.error('Please select a file and account');
      return;
    }
    const fd = new FormData();
    fd.append('file', importFile);
    try {
      const response = await axios.post(`${API}/transactions/import?account_id=${importAccountId}`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(response.data.message);
      setImportDialogOpen(false);
      setImportFile(null);
      setImportAccountId('');
      fetchData();
    } catch (error) {
      const gate = tierRequiredDetail(error);
      if (gate) {
        // Close the import dialog so the upsell modal isn't stacked behind it.
        setImportDialogOpen(false);
        setImportGateDetail(gate);
        return;
      }
      toast.error(error.response?.data?.detail || 'Failed to import transactions');
    }
  };

  const downloadTemplate = () => {
    const today = new Date().toISOString().split('T')[0];
    const exampleDate = new Date(Date.now() - MS_PER_DAY).toISOString().split('T')[0];
    const rows = [
      'date,description,amount,type,category',
      `${today},Tesco groceries,42.50,expense,Food`,
      `${today},Monthly salary,2500.00,income,Salary`,
      `${exampleDate},Costa Coffee,3.75,expense,Food`,
    ];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'forge-ledger-import-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  const openNewDialog = () => {
    setEditingItem(null);
    setFormData({
      ...emptyForm(),
      account_id: accounts[0]?.account_id || '',
    });
    setDialogOpen(true);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStartDate('');
    setEndDate('');
    setSelectedAccount('all');
    setMechanicFilter('all');
  };

  return {
    // flags
    loading,
    // state
    accounts,
    items,
    formData,
    editingItem,
    dialogOpen,
    importDialogOpen,
    pendingDelete,
    importGateDetail,
    selectedAccount,
    mechanicFilter,
    sortConfig,
    searchQuery,
    startDate,
    endDate,
    importFile,
    importAccountId,
    fileInputRef,
    // derived
    accountNameMap,
    accountKindMap,
    formIsLiability,
    // setters
    setFormData,
    setDialogOpen,
    setImportDialogOpen,
    setEditingItem,
    setPendingDelete,
    setImportGateDetail,
    setSelectedAccount,
    setMechanicFilter,
    setSearchQuery,
    setStartDate,
    setEndDate,
    setImportFile,
    setImportAccountId,
    // handlers
    toggleSort,
    handleSubmit,
    handleEdit,
    handleDelete,
    confirmDelete,
    handleFileChange,
    handleImport,
    downloadTemplate,
    openNewDialog,
    clearFilters,
    resetForm,
  };
}
